import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { MapPin, RefreshCw, Sparkles, Cloud, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONDITION_BG = {
  clear: 'linear-gradient(135deg, #FFE8A3 0%, #FFC8DD 100%)',
  partly_cloudy: 'linear-gradient(135deg, #DCECFF 0%, #FDF0D5 100%)',
  cloudy: 'linear-gradient(135deg, #E2E8F0 0%, #CDB4DB 100%)',
  rain: 'linear-gradient(135deg, #A2D2FF 0%, #CDB4DB 100%)',
  thunder: 'linear-gradient(135deg, #6B7FD7 0%, #2D2D4A 100%)',
  snow: 'linear-gradient(135deg, #F0F8FF 0%, #DCECFF 100%)',
  sleet: 'linear-gradient(135deg, #D3E5F0 0%, #A2D2FF 100%)',
  fog: 'linear-gradient(135deg, #E8E8E8 0%, #C9C9C9 100%)',
};

export default function WeatherWidget({ onPetUpdate, onWeatherUpdate }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [coords, setCoords] = useState(null);
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [applying, setApplying] = useState(false);

  // Notify parent whenever weather changes (stable callback via ref)
  const cbRef = useRef(onWeatherUpdate);
  useEffect(() => { cbRef.current = onWeatherUpdate; }, [onWeatherUpdate]);
  useEffect(() => {
    if (cbRef.current) cbRef.current(weather);
  }, [weather]);

  const getLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported by this browser'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
        (err) => reject(err),
        { timeout: 10000, maximumAge: 10 * 60 * 1000 }
      );
    });
  }, []);

  const fetchWeather = useCallback(async (c) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/weather/current`, {
        params: { lat: c.lat, lon: c.lon },
        withCredentials: true,
      });
      setWeather(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Unable to fetch weather');
    } finally {
      setLoading(false);
    }
  }, []);

  const initWeather = useCallback(async () => {
    try {
      // Load saved sync pref
      const { data: sync } = await axios.get(`${API}/weather/sync`, { withCredentials: true });
      setSyncEnabled(sync.enabled);
      if (sync.location) {
        setCoords({ lat: sync.location.lat, lon: sync.location.lon });
        if (sync.last_weather) setWeather({ ...sync.last_weather, city: sync.location.city });
      }
      // Always refresh with current geolocation if available
      try {
        const c = await getLocation();
        setCoords(c);
        await fetchWeather(c);
      } catch (geoErr) {
        if (!sync.location) {
          setError('Enable location to see your local weather');
        }
      }
    } catch (e) {
      setError('Weather unavailable');
    }
  }, [getLocation, fetchWeather]);

  useEffect(() => { initWeather(); }, [initWeather]);

  const handleRefresh = async () => {
    try {
      const c = await getLocation();
      setCoords(c);
      await fetchWeather(c);
    } catch (e) {
      toast.error('Please allow location access to refresh weather');
    }
  };

  const handleAutoEquip = async () => {
    if (!coords) {
      toast.error('Location needed first');
      return;
    }
    setApplying(true);
    try {
      const { data } = await axios.post(`${API}/weather/auto-outfit`, null, {
        params: { lat: coords.lat, lon: coords.lon },
        withCredentials: true,
      });
      const applied = Object.values(data.applied).filter(Boolean);
      if (applied.length > 0) {
        toast.success(`Dressed for ${data.weather.label.toLowerCase()}! ${data.weather.emoji}`);
      } else if (data.missing && data.missing.length > 0) {
        toast.info(`Buy ${data.missing.join(', ').replaceAll('_', ' ')} in the Shop to auto-dress!`);
      } else {
        toast.info('Perfect weather — no gear needed!');
      }
      if (onPetUpdate) await onPetUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-dress failed');
    } finally {
      setApplying(false);
    }
  };

  const toggleSync = async () => {
    const next = !syncEnabled;
    try {
      await axios.post(`${API}/weather/sync`, { enabled: next }, { withCredentials: true });
      setSyncEnabled(next);
      toast.success(next ? 'Weather sync ON' : 'Weather sync OFF');
      if (next) await handleAutoEquip();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  // Auto-apply outfit when weather sync is ON and weather updates
  useEffect(() => {
    if (syncEnabled && weather && coords && !applying) {
      // Trigger auto-equip silently
      axios
        .post(`${API}/weather/auto-outfit`, null, {
          params: { lat: coords.lat, lon: coords.lon },
          withCredentials: true,
        })
        .then(() => {
          if (onPetUpdate) onPetUpdate();
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weather?.forecast_time, syncEnabled]);

  if (error && !weather) {
    return (
      <div className="neo-card p-4 bg-white dark:bg-[#252545]" data-testid="weather-widget">
        <div className="flex items-center gap-2 text-sm">
          <AlertCircle className="w-4 h-4 text-orange-500" />
          <span className="font-semibold">{error}</span>
          <button
            onClick={handleRefresh}
            className="ml-auto neo-btn px-2 py-1 text-xs bg-[#A2D2FF]"
            data-testid="weather-retry-btn"
          >
            <MapPin className="w-3 h-3 inline mr-1" />
            Use location
          </button>
        </div>
      </div>
    );
  }

  if (!weather) {
    return (
      <div className="neo-card p-4 bg-white dark:bg-[#252545] animate-pulse-soft" data-testid="weather-widget">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Cloud className="w-4 h-4" /> Loading weather...
        </div>
      </div>
    );
  }

  const bg = CONDITION_BG[weather.condition] || CONDITION_BG.cloudy;
  const isDark = weather.condition === 'thunder';

  return (
    <div
      className="neo-card p-4 relative overflow-hidden"
      style={{ background: bg, color: isDark ? '#fff' : '#111' }}
      data-testid="weather-widget"
    >
      <div className="flex items-start gap-3">
        <div className="text-5xl leading-none select-none" data-testid="weather-emoji">
          {weather.emoji}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-3xl font-bold font-['Fredoka'] leading-none" data-testid="weather-temp">
              {Math.round(weather.temp_c)}°C
            </p>
            <button
              onClick={handleRefresh}
              className="ml-auto p-1 rounded hover:bg-white/40 transition"
              title="Refresh"
              data-testid="weather-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <p className="text-sm font-semibold mt-1" data-testid="weather-label">{weather.label}</p>
          {weather.city && (
            <p className="text-xs flex items-center gap-1 mt-0.5 opacity-80" data-testid="weather-city">
              <MapPin className="w-3 h-3" /> {weather.city}
            </p>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2 items-center">
        <button
          onClick={handleAutoEquip}
          disabled={applying}
          className="neo-btn px-3 py-1.5 bg-white text-black text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
          data-testid="weather-dress-btn"
        >
          <Sparkles className="w-3.5 h-3.5" />
          {applying ? 'Dressing...' : 'Dress my pet'}
        </button>
        <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer select-none" data-testid="weather-sync-toggle">
          <input
            type="checkbox"
            checked={syncEnabled}
            onChange={toggleSync}
            className="w-4 h-4 accent-pink-500"
          />
          Auto-sync
        </label>
        <span className="ml-auto text-[10px] opacity-70">
          {weather.source === 'smhi' ? '🇸🇪 SMHI' : 'Open-Meteo'}
        </span>
      </div>
    </div>
  );
}
